import { useMemo } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ConversationDetails } from "@/types/conversation/conversation";
import { ConversationDigest, DigestHighlight, DigestHighlightKind } from "@/types/activity";
import { buildAttentionQueue, buildPulseAggregates, buildTopicTrends } from "../utils/aggregates";
import { InsightsActionQueue } from "./InsightsActionQueue";
import { InsightsCategoryBreakdown } from "./InsightsCategoryBreakdown";
import { InsightsPulseStrip } from "./InsightsPulseStrip";
import { InsightsRecurringSignals } from "./InsightsRecurringSignals";
import { InsightsSmartPrompts } from "./InsightsSmartPrompts";
import { InsightsTimeDistribution } from "./InsightsTimeDistribution";
import { InsightsTopicTrends } from "./InsightsTopicTrends";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

const MASCOT_SRC = "/genie-mascots/role/support.png";

const DATE_RANGE_LABEL: Record<string, string> = {
  "7d": "the last 7 days",
  "30d": "the last 30 days",
  "90d": "the last 90 days",
  all: "all time",
};

interface InsightsDigestProps {
  conversations: ConversationDetails[];
  isLoading: boolean;
  dateRange: string;
  selectedGenieName: string | null;
  onAskTopic: (topic: string) => void;
  onAskSignal: (signal: string) => void;
  onSmartPrompt: (prompt: string) => void;
  /** AI-synthesized digest (optional — falls back to a client summary). */
  digest?: ConversationDigest | null;
  digestLoading?: boolean;
}

/**
 * The Conversational Insights "digest" — the briefing treatment for a whole period.
 *
 * Leads with a genie-voiced *story* (AI-synthesized when available, client-computed otherwise),
 * then decision-shaped highlight cards and the supporting trends. The raw drill-down table lives
 * in its own Conversations tab now — this view is purely insight.
 */

// Order so wins/opportunities lead and "watch" items are demoted — same principle as the briefing.
const HIGHLIGHT_ORDER: Record<DigestHighlightKind, number> = { win: 0, opportunity: 1, theme: 2, watch: 3 };

const HIGHLIGHT_STYLE: Record<DigestHighlightKind, { badge: string; label: string; genieIcon: GenieIconName }> = {
  win: { badge: "bg-genie-primary/10 text-genie-primary", label: "Win", genieIcon: "smile" },
  opportunity: { badge: "bg-genie-accent/15 text-[#B57A00]", label: "Opportunity", genieIcon: "target" },
  theme: { badge: "bg-gray-100 text-gray-600", label: "Theme", genieIcon: "chat" },
  watch: { badge: "bg-gray-100 text-gray-600", label: "Worth a look", genieIcon: "eye" },
};

const buildFallbackStory = (
  conversations: ConversationDetails[],
  dateRange: string,
  genieName: string | null,
): string => {
  const pulse = buildPulseAggregates(conversations, dateRange);
  const topics = buildTopicTrends(conversations, dateRange, 1);
  const attention = buildAttentionQueue(conversations);
  const count = pulse.thisPeriodCount || pulse.total;
  const scope = genieName ? genieName : "your Genies";
  const range = DATE_RANGE_LABEL[dateRange] ?? "this period";

  if (count === 0) return "";

  const parts: string[] = [
    `Over ${range}, ${scope} handled ${count} conversation${count === 1 ? "" : "s"}.`,
  ];

  if (pulse.sentimentMix.total > 0) {
    const posPct = Math.round((pulse.sentimentMix.positive / pulse.sentimentMix.total) * 100);
    const negPct = Math.round((pulse.sentimentMix.negative / pulse.sentimentMix.total) * 100);
    if (posPct >= negPct + 15) parts.push("Sentiment skewed positive — customers mostly left satisfied.");
    else if (negPct >= posPct + 15) parts.push("A few callers left unhappy — there are conversations worth revisiting below.");
    else parts.push("Sentiment was steady across the board.");
  }

  if (topics[0]) {
    parts.push(`The topic that came up most was ${topics[0].topic}.`);
  }

  if (attention.length > 0) {
    parts.push(`${attention.length} conversation${attention.length === 1 ? "" : "s"} are worth a closer look.`);
  }

  return parts.join(" ");
};

const StoryHero = ({
  story,
  storyLoading,
  basedOn,
  isAi,
}: {
  story: string;
  storyLoading?: boolean;
  basedOn: number;
  isAi: boolean;
}) => (
  <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
    <div className="rounded-[14px] bg-white px-5 py-5 sm:px-6">
      <div className="flex items-center gap-2.5 mb-3">
        <img src={MASCOT_SRC} alt="" className="h-7 w-7 rounded-lg object-contain flex-shrink-0" />
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-[#222B45] leading-tight">The story this period</h2>
          <p className="text-[11px] text-gray-500">
            {isAi ? `Read across ${basedOn} conversation${basedOn === 1 ? "" : "s"} by your Genie` : "A quick read of your conversations"}
          </p>
        </div>
      </div>

      {storyLoading ? (
        <div className="space-y-2">
          <div className="h-3.5 w-full bg-gray-100 rounded animate-pulse" />
          <div className="h-3.5 w-11/12 bg-gray-100 rounded animate-pulse" />
          <div className="h-3.5 w-3/4 bg-gray-100 rounded animate-pulse" />
        </div>
      ) : (
        <p className="text-[15px] leading-relaxed text-gray-700">{story}</p>
      )}
    </div>
  </div>
);

const HighlightCard = ({ highlight }: { highlight: DigestHighlight }) => {
  const style = HIGHLIGHT_STYLE[highlight.kind];
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 hover:border-genie-primary/30 hover:shadow-md transition-all">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-genie-primary/[0.08] to-genie-secondary/[0.08] flex items-center justify-center flex-shrink-0">
          <GenieIcon name={style.genieIcon} size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${style.badge}`}>
              {style.label}
            </span>
            {typeof highlight.count === "number" && highlight.count > 1 && (
              <span className="text-[11px] text-gray-400">· {highlight.count} calls</span>
            )}
          </div>
          <h4 className="text-sm font-semibold text-gray-900 leading-snug">{highlight.title}</h4>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed">{highlight.detail}</p>
          {highlight.examples && highlight.examples.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {highlight.examples.slice(0, 3).map((ex, i) =>
                ex.id ? (
                  <Link
                    key={`${ex.id}-${i}`}
                    to={`/genie-hub/conversations/${ex.id}`}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-genie-primary bg-genie-primary/5 hover:bg-genie-primary/10 rounded-full px-2 py-0.5 transition-colors"
                  >
                    {ex.label}
                    <ArrowUpRight className="w-3 h-3" />
                  </Link>
                ) : (
                  <span key={i} className="inline-flex items-center text-[11px] text-gray-500 bg-gray-50 rounded-full px-2 py-0.5">
                    {ex.label}
                  </span>
                ),
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DigestEmptyState = ({ selectedGenieName }: { selectedGenieName: string | null }) => (
  <div className="p-[2px] rounded-2xl bg-gradient-to-r from-genie-primary/40 to-genie-secondary/40">
    <div className="rounded-[14px] bg-white px-6 py-10 text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-genie-primary/10 mb-4">
        <img src="/genie-mascots/role/support.png" alt="" className="h-9 w-9 object-contain" />
      </div>
      <h3 className="text-lg font-bold text-[#222B45] mb-1">
        No conversations to read yet{selectedGenieName ? ` for ${selectedGenieName}` : ""}
      </h3>
      <p className="text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
        Once people start talking to your Genies, this is where the story lands — the themes customers
        keep raising, where your Genie is winning, and the leads worth chasing. Share a link or QR code to
        get the first conversations flowing.
      </p>
    </div>
  </div>
);

export const InsightsDigest = ({
  conversations,
  isLoading,
  dateRange,
  selectedGenieName,
  onAskTopic,
  onAskSignal,
  onSmartPrompt,
  digest,
  digestLoading,
}: InsightsDigestProps) => {
  const fallbackStory = useMemo(
    () => buildFallbackStory(conversations, dateRange, selectedGenieName),
    [conversations, dateRange, selectedGenieName],
  );

  const sortedHighlights = useMemo(
    () =>
      (digest?.highlights ?? [])
        .slice()
        .sort((a, b) => HIGHLIGHT_ORDER[a.kind] - HIGHLIGHT_ORDER[b.kind]),
    [digest],
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-[140px] rounded-2xl bg-gray-50 animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[120px] rounded-2xl bg-gray-50 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return <DigestEmptyState selectedGenieName={selectedGenieName} />;
  }

  // Prefer the AI story; fall back to the client-computed one while it loads or if it's unavailable.
  const storyText = digest?.story || fallbackStory;
  const isAi = !!digest?.story;
  const storyLoading = !!digestLoading && !digest?.story && !fallbackStory;
  // The pulse/category cards start out rendering the client-side aggregate immediately (fast,
  // but blind to conversations that only got classified by the digest call). Once the digest
  // request is in flight, hold a loading state instead of flashing those numbers then swapping
  // them out when the richer digest data lands.
  const classificationLoading = !!digestLoading && !digest?.classificationStats;

  return (
    <div className="space-y-6">
      <StoryHero
        story={storyText}
        storyLoading={storyLoading}
        basedOn={digest?.basedOn ?? conversations.length}
        isAi={isAi}
      />

      <InsightsPulseStrip
        conversations={conversations}
        dateRange={dateRange}
        isLoading={false}
        digestResolution={digest?.classificationStats?.resolution}
        resolutionLoading={classificationLoading}
      />

      {/* AI-synthesized highlights — the decision-shaped findings, when available. */}
      {sortedHighlights.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3 px-1">
            <h3 className="text-base font-semibold text-gray-900">What stood out</h3>
            <span className="text-xs text-gray-500">· synthesized across your conversations</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {sortedHighlights.map((h, i) => (
              <HighlightCard key={`${h.kind}-${i}`} highlight={h} />
            ))}
          </div>
        </section>
      )}

      {/* Supporting trends + the conversations worth a closer look. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <InsightsTopicTrends conversations={conversations} dateRange={dateRange} onAskAbout={onAskTopic} />
        <InsightsCategoryBreakdown
          conversations={conversations}
          digestBreakdown={digest?.classificationStats?.categoryBreakdown}
          isLoading={classificationLoading}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <InsightsTimeDistribution conversations={conversations} />
        <InsightsRecurringSignals conversations={conversations} onAskAbout={onAskSignal} />
      </div>

      <InsightsActionQueue conversations={conversations} />

      <InsightsSmartPrompts
        genieName={selectedGenieName}
        dateRange={dateRange}
        conversationCount={conversations.length}
        onAsk={onSmartPrompt}
      />
    </div>
  );
};
